import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, CalendarClock, History, Minus, Plus, MapPin } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/api";
import { useCart } from "@/context/CartContext";
import { useSchedule } from "@/context/ScheduleContext";
import StatusBadge from "@/components/StatusBadge";
import ProtectedImage from "@/components/ProtectedImage";
import ScheduleStrip from "@/components/ScheduleStrip";

const fmt = (iso) =>
  new Date(iso).toLocaleString("id-ID", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });

export default function ItemDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [avail, setAvail] = useState(null);
  const { setQty, qtyOf } = useCart();
  const sched = useSchedule();

  useEffect(() => {
    setNotFound(false);
    api.get(`/items/${id}`).then(({ data }) => setItem(data))
      .catch((e) => {
        if (e?.response?.status === 404) setNotFound(true);
        else toast.error(errMsg(e));
      });
  }, [id]);

  // Ketersediaan untuk rentang aktif diambil dari cek rentang yang sama
  // dengan katalog (endpoint detail tidak menerima rentang).
  useEffect(() => {
    if (!sched.ok || !item) { setAvail(null); return undefined; }
    let alive = true;
    api.get("/items", { params: { start_time: sched.pickup, end_time: sched.ret } })
      .then(({ data }) => {
        if (!alive) return;
        const row = data.find((r) => r.id === item.id);
        setAvail(row ? (row.available_qty ?? row.quantity) : null);
      })
      .catch(() => { if (alive) setAvail(null); });
    return () => { alive = false; };
  }, [sched.ok, sched.pickup, sched.ret, item]);

  if (notFound)
    return (
      <div className="rounded-xl border border-dashed border-slate-300 bg-white p-7 text-center">
        <p className="font-semibold text-slate-900">Alat tidak ditemukan.</p>
        <p className="mt-1 text-sm text-slate-500">Tautan ini tidak menunjuk ke alat mana pun.</p>
        <button onClick={() => navigate("/")} className="mt-3 min-h-[44px] rounded-lg border border-slate-300 bg-white px-4 text-sm font-semibold text-slate-900">Kembali ke katalog</button>
      </div>
    );
  if (!item) return <p className="text-sm text-slate-500">Memuat…</p>;
  const qty = qtyOf(item.id);
  const maintenance = item.status === "MAINTENANCE";
  const cap = sched.ok && avail != null ? avail : item.quantity;
  const conflict = sched.ok && avail != null && qty > avail;

  const availLine = maintenance ? null : !sched.ok
    ? `Stok total ${item.quantity} unit. Pilih jadwal untuk cek sisa`
    : avail == null
      ? "Memeriksa sisa untuk jadwal ini…"
      : avail <= 0
        ? `Habis untuk jadwal ini (stok total ${item.quantity})`
        : `Sisa ${avail} dari ${item.quantity} unit untuk jadwal ini`;

  return (
    <div className="space-y-6 pb-24">
      <button data-testid="back-button" onClick={() => navigate("/")}
        className="flex min-h-[44px] items-center gap-2 text-sm font-semibold text-[#004f9e] hover:underline">
        <ArrowLeft className="h-4 w-4" /> Kembali ke katalog
      </button>

      <div className="grid items-start gap-4 lg:grid-cols-[1fr_360px]">
        <div className="min-w-0 rounded-lg border border-slate-200 bg-white p-5">
          <p className="font-mono text-xs text-slate-500">{item.item_code} · {item.category}</p>
          <h1 data-testid="item-detail-name" className="font-heading mt-1 text-[26px] font-semibold tracking-tight text-slate-900">
            {item.name}
          </h1>
          <div className="mt-4 overflow-hidden rounded-lg bg-slate-100">
            <ProtectedImage src={item.photo} alt={item.name} className="block aspect-[4/3] w-full object-cover" />
          </div>
          <p className="mt-3 text-sm text-slate-500">
            Stok {item.quantity} pcs · kondisi {item.condition}
          </p>
          <p className="mt-1 flex items-center gap-2 text-sm text-slate-500"><MapPin className="h-4 w-4" /> {item.location}</p>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <StatusBadge status={item.status} testId="item-detail-status" tone="light" />
            {availLine && (
              <span data-testid="item-detail-availability" className={`text-[13px] ${conflict || (sched.ok && avail <= 0) ? "font-bold text-rose-700" : sched.ok && avail <= 1 ? "font-bold text-amber-800" : sched.ok ? "font-semibold text-emerald-700" : "text-slate-500"}`}>
                {availLine}
              </span>
            )}
          </div>

          <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5">
            <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
              <CalendarClock className="h-4 w-4" /> Jadwal Terpakai
            </h3>
            <div data-testid="item-schedule" className="mt-4 space-y-2">
              {item.schedule?.length ? item.schedule.map((s) => (
                <div key={s.booking_id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-slate-100 px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{fmt(s.start_time)} s.d. {fmt(s.end_time)}</p>
                    <p className="text-xs text-slate-500">{s.user_name} · #{s.code} · {s.qty} pcs</p>
                  </div>
                  <StatusBadge status={s.status} tone="light" />
                </div>
              )) : <p className="text-sm text-slate-500">Belum ada jadwal. Alat bebas dipinjam.</p>}
            </div>
          </div>

          <div className="mt-4 rounded-xl border border-slate-200 bg-white p-5">
            <h3 className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
              <History className="h-4 w-4" /> Riwayat Peminjaman
            </h3>
            <div data-testid="item-history" className="mt-4 space-y-2">
              {item.history?.length ? item.history.map((h) => (
                <div key={h.booking_id} data-testid={`history-row-${h.code}`}
                  className="flex flex-wrap items-center justify-between gap-3 rounded-lg bg-slate-100 px-4 py-3">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{h.user_name}</p>
                    <p className="text-xs text-slate-500">
                      #{h.code} · {fmt(h.start_time)} s.d. {fmt(h.end_time)}
                      {h.return_condition ? ` · kondisi ${h.return_condition}` : ""}
                    </p>
                    {h.purpose && <p className="mt-1 text-xs text-slate-500">{h.purpose}</p>}
                  </div>
                  <StatusBadge status={h.status} tone="light" />
                </div>
              )) : <p className="text-sm text-slate-500">Belum ada riwayat peminjaman.</p>}
            </div>
          </div>
        </div>

        <div className="space-y-4 rounded-lg border border-slate-200 bg-white p-5 lg:sticky lg:top-20">
          <ScheduleStrip />
          <h2 className="font-heading text-[19px] font-semibold tracking-tight text-slate-900">Tambah ke Pesanan</h2>

          {maintenance ? (
            <p className="text-sm font-semibold text-rose-700">Alat sedang maintenance dan tidak bisa dipesan.</p>
          ) : (
            <>
              <div className="flex items-center justify-between rounded-xl border border-slate-300 px-4 py-2">
                <button data-testid="detail-dec-button" onClick={() => setQty(item, qty - 1)} disabled={qty === 0}
                  className="min-h-[44px] min-w-[44px] rounded-lg text-xl text-slate-900 hover:bg-slate-100 disabled:opacity-30">
                  −
                </button>
                <span data-testid="detail-qty" className="font-mono text-lg tabular-nums">{qty} pcs</span>
                <button data-testid="detail-inc-button" onClick={() => setQty(item, qty + 1)}
                  disabled={qty >= cap}
                  title={sched.ok && avail != null && qty >= avail ? "Sudah mencapai sisa untuk jadwal ini" : undefined}
                  className="min-h-[44px] min-w-[44px] rounded-lg text-xl text-slate-900 hover:bg-slate-100 disabled:opacity-30">
                  +
                </button>
              </div>
              {conflict && (
                <p className="rounded-lg border border-[#e8c95c] bg-[#fff8e6] p-2.5 text-[13px] font-semibold text-[#6b4e0c]">
                  Bentrok: butuh {qty}, sisa {avail}. Kurangi jumlah atau ubah jadwal.
                </p>
              )}
              {qty === 0 && !maintenance && (
                <p className="text-sm text-slate-500">Pilih jumlah dulu, atau lanjut untuk melihat katalog.</p>
              )}
              <button data-testid="detail-checkout-button" onClick={() => navigate(qty > 0 ? "/checkout" : "/")}
                className="h-14 w-full rounded-lg border border-[#0067c8] bg-[#0067c8] text-base font-semibold text-white transition-colors duration-200 hover:border-[#004f9e] hover:bg-[#004f9e]">
                {qty > 0 ? "Lanjut ke Keranjang" : "Kembali ke katalog"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
